import { GroupeFormData } from "@repo/types";
import { Badge } from "@repo/ui/components/ui/badge";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@repo/ui/components/ui/radio-group";
import { cn } from "@repo/ui/lib/utils";
import { Package, Shield, Crown } from "lucide-react";

interface GroupeInfoSectionProps {
  formData: GroupeFormData;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
}

export function GroupeInfoSection({
  formData,
  handleInputChange,
  handleSelectChange
}: GroupeInfoSectionProps) {
  return (
    <div className="space-y-6">
      {/* Nom du groupe */}
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium">
          Nom du groupe <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          name="name"
          value={formData.name || ""}
          onChange={handleInputChange}
          placeholder="Ex: Équipe Marketing"
          className="h-11"
        />
        {!formData.name && (
          <p className="text-xs text-red-600">Ce champ est obligatoire</p>
        )}
      </div>

      {/* Nombre de licences */}
      <div className="space-y-2">
        <Label htmlFor="licence" className="text-sm font-medium">
          Nombre de licences <span className="text-red-500">*</span>
        </Label>
        <Input
          id="licence"
          name="licence"
          type="number"
          min="1"
          value={formData.licence || ""}
          onChange={(e) => handleInputChange({
            ...e,
            target: {
              ...e.target,
              name: "licence",
              value: e.target.value ? parseInt(e.target.value) : "",
            } as any,
          })}
          placeholder="Ex: 10"
          className="h-11"
        />
        <p className="text-xs text-muted-foreground">Nombre d'utilisateurs autorisés</p>
      </div>

      {/* Type de groupe */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Type de groupe</Label>
        <RadioGroup
          value={formData.groupe_type || "DENDROMAP"}
          onValueChange={(value) => handleSelectChange("groupe_type", value)}
        >
          <div className="flex flex-col gap-3">
            <div className={cn(
              "flex items-center space-x-3 px-4 py-3 border-2 rounded-lg transition-all cursor-pointer",
              formData.groupe_type === "ADMIN"
                ? "border-purple-500 bg-purple-50"
                : "border-gray-200 hover:border-gray-300"
            )}>
              <RadioGroupItem value="ADMIN" id="admin" />
              <Label htmlFor="admin" className="flex items-center gap-2 cursor-pointer flex-1">
                <Shield className="h-4 w-4 text-purple-600" />
                <span className="font-medium">Admin</span>
                <Badge variant="secondary" className="text-xs ml-auto">Accès complet</Badge>
              </Label>
            </div>

            <div className={cn(
              "flex items-center space-x-3 px-4 py-3 border-2 rounded-lg transition-all cursor-pointer",
              formData.groupe_type === "DENDROMAP"
                ? "border-primary bg-primary/5"
                : "border-gray-200 hover:border-gray-300"
            )}>
              <RadioGroupItem value="DENDROMAP" id="dendromap" />
              <Label htmlFor="dendromap" className="flex items-center gap-2 cursor-pointer flex-1">
                <Package className="h-4 w-4 text-primary" />
                <span className="font-medium">Dendromap</span>
              </Label>
            </div>

            <div className={cn(
              "flex items-center space-x-3 px-4 py-3 border-2 rounded-lg transition-all cursor-pointer",
              formData.groupe_type === "PREMIUM"
                ? "border-amber-500 bg-amber-50"
                : "border-gray-200 hover:border-gray-300"
            )}>
              <RadioGroupItem value="PREMIUM" id="premium" />
              <Label htmlFor="premium" className="flex items-center gap-2 cursor-pointer flex-1">
                <Crown className="h-4 w-4 text-amber-600" />
                <span className="font-medium">Premium</span>

              </Label>
            </div>
          </div>
        </RadioGroup>
      </div>
    </div>
  );
}

